// Router (history API) — /  /c/:cat  /p/:sku  /checkout  /order/:order_no  /admin
import { lazy, Suspense, useEffect, useState } from 'react';
import { I18nProvider } from './lib/i18n.jsx';
import { StoreProvider, useStore } from './lib/store.jsx';
import { AnnounceBar, Header, Footer, Overlays } from './components/Chrome.jsx';
import Home from './pages/Home.jsx';
import Category from './pages/Category.jsx';
import Product from './pages/Product.jsx';
import Checkout from './pages/Checkout.jsx';
import Confirm from './pages/Confirm.jsx';
import Track from './pages/Track.jsx';
import Policy from './pages/Policy.jsx';
import { setPageMeta, titleFor } from './lib/seo-client.js';

const AdminApp = lazy(() => import('./admin/AdminApp.jsx'));

function usePath() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const nav = (to) => {
    if (to !== window.location.pathname) window.history.pushState({}, '', to);
    setPath(to);
    window.scrollTo(0, 0);
  };
  return [path, nav];
}

function Routes({ path, nav }) {
  const store = useStore();
  const { setDrawerOpen } = store;
  useEffect(() => { setDrawerOpen(false); }, [path]);
  useEffect(() => { setPageMeta(titleFor(path, store)); }, [path, store.products]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const seg = path.split('/').filter(Boolean);
  if (seg[0] === 'c') return <Category cat={seg[1] || 'all'} nav={nav} />;
  if (seg[0] === 'p') return <Product sku={decodeURIComponent(seg[1] || '')} nav={nav} />;
  if (seg[0] === 'checkout') return <Checkout nav={nav} />;
  if (seg[0] === 'order') return <Confirm orderNo={decodeURIComponent(seg[1] || '')} nav={nav} />;
  if (seg[0] === 'track') return <Track nav={nav} />;
  if (seg[0] === 'help') return <Policy slug={seg[1] || 'faq'} nav={nav} />;
  return <Home nav={nav} />;
}

export default function App() {
  const [path, nav] = usePath();

  // Admin เป็นแอปแยก (ไม่มี chrome หน้าร้าน) — โหลดแยก bundle
  if (path.split('/').filter(Boolean)[0] === 'admin') {
    return <Suspense fallback={<div style={{ padding: 60, textAlign: 'center', fontFamily: 'monospace' }}>…</div>}><AdminApp /></Suspense>;
  }

  return (
    <I18nProvider>
      <StoreProvider>
        <AnnounceBar />
        <Header nav={nav} />
        <main>
          <Routes path={path} nav={nav} />
        </main>
        <Footer nav={nav} />
        <Overlays nav={nav} />
      </StoreProvider>
    </I18nProvider>
  );
}
